import type { ReactNode } from "react";

/**
 * The Markdown subset Hub AI answers and drafts use: paragraphs, headings, lists, quotes, code,
 * links, bold, italic, strikethrough. Slack's send tool takes the same standard Markdown.
 */
type Block =
  | { type: "p"; lines: string[] }
  | { type: "h"; text: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "quote"; lines: string[] }
  | { type: "code"; text: string };

const SAFE_HREF = /^https?:\/\//i;

// Order matters: links and code win over emphasis inside them. `_italic_` needs non-word
// characters around it so snake_case names like slack_read_channel stay as they are.
const INLINE =
  /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|\/(?!\/)[\w\-./?=&#%]*)\)|<(https?:\/\/[^|>\s]+)(?:\|([^>\n]+))?>|`([^`\n]+)`|\*\*(?!\s)([^*\n]+?)\*\*|(?<![\w*])\*(?!\s)([^*\n]+?)\*(?![\w*])|(?<![\w_])_(?!\s)([^_\n]+?)_(?![\w_])|~~(?!\s)([^~\n]+?)~~|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/gi;

/** In-app Hub page, e.g. `/tasks` — the Hub AI panel routes these without a reload. */
const HUB_ROUTE = /^\/(?!\/)[\w\-./?=&#%]*$/;

function link(href: string, label: string, key: string): ReactNode {
  if (HUB_ROUTE.test(href)) {
    return (
      <a key={key} href={href} data-hub-route="" className="chat-link">
        {label}
      </a>
    );
  }
  if (!SAFE_HREF.test(href)) return label;
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="chat-link">
      {label}
    </a>
  );
}

function renderInline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0;
    if (index > last) out.push(text.slice(last, index));
    const key = `${keyBase}-${n++}`;
    const [, mdLabel, mdHref, slackHref, slackLabel, code, bold, starItalic, lineItalic, strike, url] = match;
    const italic = starItalic || lineItalic;
    if (mdHref) out.push(link(mdHref, mdLabel || mdHref, key));
    else if (slackHref) out.push(link(slackHref, slackLabel || slackHref, key));
    else if (code) {
      out.push(
        <code key={key} className="chat-code">
          {code}
        </code>,
      );
    } else if (bold) out.push(<strong key={key}>{renderInline(bold, key)}</strong>);
    else if (italic) out.push(<em key={key}>{italic}</em>);
    else if (strike) out.push(<del key={key}>{strike}</del>);
    else if (url) out.push(link(url, url, key));
    last = index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  let open = false;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!;
    const prev = blocks[blocks.length - 1];
    if (/^\s*```/.test(line)) {
      const body: string[] = [];
      for (i += 1; i < lines.length && !/^\s*```/.test(lines[i]!); i += 1) body.push(lines[i]!);
      blocks.push({ type: "code", text: body.join("\n") });
      open = false;
      continue;
    }
    if (!line.trim()) {
      open = false;
      continue;
    }
    const heading = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/.exec(line);
    if (heading) {
      blocks.push({ type: "h", text: heading[1]! });
      open = false;
      continue;
    }
    const item = /^\s*(?:([-*•])|\d+[.)])\s+(.*)$/.exec(line);
    if (item) {
      const ordered = !item[1];
      if (open && prev?.type === "list" && prev.ordered === ordered) prev.items.push(item[2]!);
      else blocks.push({ type: "list", ordered, items: [item[2]!] });
      open = true;
      continue;
    }
    const quote = /^\s*>\s?(.*)$/.exec(line);
    if (quote) {
      if (open && prev?.type === "quote") prev.lines.push(quote[1]!);
      else blocks.push({ type: "quote", lines: [quote[1]!] });
      open = true;
      continue;
    }
    if (open && prev?.type === "list" && /^\s{2,}\S/.test(line)) {
      prev.items[prev.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    if (open && prev?.type === "p") prev.lines.push(line);
    else blocks.push({ type: "p", lines: [line] });
    open = true;
  }
  return blocks;
}

function renderLines(lines: string[], key: string): ReactNode[] {
  return lines.flatMap((line, i) => {
    const inline = renderInline(line, `${key}-${i}`);
    return i ? [<br key={`${key}-br${i}`} />, ...inline] : inline;
  });
}

export function renderAiText(text: string): ReactNode[] {
  return parseBlocks(text).map((block, index) => {
    const key = `b${index}`;
    switch (block.type) {
      case "h":
        return (
          <p key={key} className="hub-ai-heading">
            {renderInline(block.text, key)}
          </p>
        );
      case "code":
        return (
          <pre key={key} className="chat-code-block">
            {block.text}
          </pre>
        );
      case "quote":
        return (
          <blockquote key={key} className="hub-ai-quote">
            {renderLines(block.lines, key)}
          </blockquote>
        );
      case "list": {
        const items = block.items.map((item, i) => <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>);
        return block.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>;
      }
      default:
        return <p key={key}>{renderLines(block.lines, key)}</p>;
    }
  });
}
